/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Modal,
  ModalHeader,
  Cross,
  ModalContent,
  Space,
  Text,
  Button,
  Conditional,
} from '@cashfree-intl/coherent';

import useClickOutside from '../../../utils/hooks';
import CustomIcon from '../../../components/CustomIcons';
import EVENTS from '../../../constants/analytics';
import Analytics from '../../../utils/analytics';
import { INSTANT } from '../../../utils/constants';

const FAQs = [
  {
    question: 'Is there a limit on the amount settled in instant settlements?',
    answer:
      'Yes, there is a daily limit on the instant settlement amount. The limit is decided based on the business volume.',
  },
  {
    question: 'Are there any additional charges for instant settlements?',
    answer: [
      'Pricing starts at 0.3 % of the settlement amount.',
      'Instant settlement charges are debited from the settlement amount at the time of settlement.',
    ],
  },
  {
    question: 'Are instant settlements available on holidays?',
    answer:
      'Instant settlements are available on all days, 24x7. It is also available on working days only.',
  },
  {
    question: 'How long does it take to activate Instant Settlements?',
    answer:
      'Instant Settlements is activated as soon as you request activation. Standard rates and daily limits are applicable until we review your request. After we review your request, the limits, and rates, will be upgraded automatically.',
  },
  {
    question: 'How can I know if a settlement was instant or standard?',
    answer: () => (
      <Text variant="b12" color="white" className="answer mt-1">
        Instant settlements are marked with{' '}
        <CustomIcon componentName="LIGHTNING" /> icon in the list of settlements
        shown in the Settlements section. You can also filter for settlements
        based on the type in the Settlements section.
      </Text>
    ),
  },
];

const FAQModal = ({ closeModal, onRequestActivation }) => {
  const [activeIndex, resetIndex] = useState('');

  const modalRef = useRef(null);
  useClickOutside(modalRef, closeModal);

  const onQuestionClick = index => () => {
    if (activeIndex !== index && FAQs[index])
      Analytics.track(EVENTS.SETTLEMENTS[INSTANT].VIEW_FAQ_ANSWER, {
        question: FAQs[index].question,
      });
    resetIndex(activeIndex === index ? '' : index);
  };

  return (
    <Modal open $maxWidth={649} className="faq-modal" ref={modalRef}>
      <ModalHeader>
        Instant Settlements{' '}
        <Cross
          onClick={closeModal}
          data-event-name="Form_FAQModal_Icon_Close"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent className="p-0">
        <div className="faq-section p-3">
          <Text variant="b14" strong color="white" className="mb-3">
            FAQs about Instant Settlements
          </Text>
          {FAQs.map((obj, index) => (
            <Space
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="pointer mb-3"
              alignItems="flex-start"
              gap={1}
              onClick={onQuestionClick(index)}
              data-event-name="Card_FAQModal_Click"
              data-event-properties={JSON.stringify({
                card_id: index,
                question: obj.question,
                answer: obj.answer,
              })}
              data-ignore-inner-text="true"
            >
              <div
                className={`faq-item ${activeIndex === index ? 'active' : ''}`}
              >
                <CustomIcon componentName="CHEVRONDOWN" />
              </div>
              <div>
                <Text className="question" variant="b12" color="white">
                  {obj.question}
                </Text>
                <Conditional if={activeIndex === index}>
                  {typeof obj.answer === 'string' && (
                    <Text variant="b12" color="white" className="answer mt-1">
                      {obj.answer}
                    </Text>
                  )}
                  {typeof obj.answer === 'function' && obj.answer()}
                  {Array.isArray(obj.answer) && (
                    <ul>
                      {obj.answer.map((answer, i) => (
                        <li
                          key={i}
                          className="small-text w-600 text-white answer mt-1"
                        >
                          {answer}
                        </li>
                      ))}
                    </ul>
                  )}
                </Conditional>
              </div>
            </Space>
          ))}
        </div>
        <Space
          // direction="column"
          className="request-activation-section p-3"
          alignItems="center"
        >
          <div className="text-center">
            <Text className="mb-2">
              Want to receive your settlements instantly?
            </Text>
            <Button
              primary
              onClick={onRequestActivation}
              data-event-name="Primary_Button_ActivateInstantly"
            >
              Activate Instantly
            </Button>
          </div>
        </Space>
      </ModalContent>
    </Modal>
  );
};

FAQModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onRequestActivation: PropTypes.func.isRequired,
};

export default FAQModal;
